/**
 * 单条待审批 / 审批实例卡。
 *
 * SLA：后端列表接口未返回 deadline，这里按「各节点 SLA 累计」
 *      给出**估算**剩余时间（节点未配置 SLA 时不展示）。
 */

import Badge from '../../components/Badge';
import Button from '../../components/Button';
import { useAuthStore } from '../../stores/auth';
import type { ApprovalInstanceInfo } from '@atlaspm/shared';
import type { KeyboardEvent } from 'react';

export interface ApprovalCardProps {
  item: ApprovalInstanceInfo;
  /** 显示操作按钮（待办 tab） */
  actionabled?: boolean;
  /** ⭐ P8.20 点击卡片查看对应任务详情（复用全局任务抽屉） */
  onOpen?(item: ApprovalInstanceInfo): void;
  onApprove?(item: ApprovalInstanceInfo): void;
  onReject?(item: ApprovalInstanceInfo): void;
  /** ⭐ P6.16 终止已超时(EXPIRED)的审批 */
  onTerminate?(item: ApprovalInstanceInfo): void;
}

function fmtAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso.slice(0, 16).replace('T', ' ');
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  return `${Math.floor(hours / 24)} 天前`;
}

function estimateSla(item: ApprovalInstanceInfo): string | null {
  const nodes = item.nodes ?? [];
  const current = nodes.find((n) => n.seq === item.currentNodeSeq);
  if (!current) return null;

  let cumulative = 0;
  let hasSla = false;
  for (const n of nodes) {
    if (n.seq > item.currentNodeSeq) break;
    if (n.slaHours != null) {
      cumulative += n.slaHours;
      hasSla = true;
    }
  }
  if (!hasSla) return null;

  const deadline = new Date(item.startedAt).getTime() + cumulative * 3600_000;
  const remainMs = deadline - Date.now();
  if (remainMs <= 0) {
    const over = Math.round(-remainMs / 3600_000);
    return `已超 SLA ${over}h`;
  }
  return `剩余 ${(remainMs / 3600_000).toFixed(1)}h`;
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'alert' | 'idle'> = {
  APPROVING: 'warn',
  APPROVED: 'ok',
  REJECTED: 'alert',
  CANCELLED: 'idle',
  EXPIRED: 'alert',
  PENDING: 'idle',
};

const STATUS_LABEL: Record<string, string> = {
  APPROVING: '审批中',
  APPROVED: '已通过',
  REJECTED: '已驳回',
  CANCELLED: '已撤回',
  EXPIRED: '已超时',
  PENDING: '待进入',
};

export default function ApprovalCard({
  item,
  actionabled,
  onOpen,
  onApprove,
  onReject,
  onTerminate,
}: ApprovalCardProps) {
  const nodes = item.nodes ?? [];
  const total = nodes.length;
  const currentNode = nodes.find((n) => n.seq === item.currentNodeSeq);
  const sla = estimateSla(item);
  const overdue = sla?.startsWith('已超');

  // ⭐ P6.13 四眼分离：提交人不能审批（同意/退回）自己提交的任务。
  //    后端 APPROVAL_SELF_NOT_ALLOWED 才是安全边界，这里只是体验层置灰 + 提示。
  const userId = useAuthStore((s) => s.user?.userId);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const isSubmitter = userId != null && item.startedBy === userId;

  // ⭐ P6.16 终止超时审批：提交人本人 / 有任务或审批管理权限者可操作，
  //    且仅当实例已超时(EXPIRED)。后端才是安全边界，这里只是体验层入口。
  const canTerminate =
    item.status === 'EXPIRED' &&
    (isSubmitter ||
      hasAnyPermission(['task:update', 'approval:approve', 'approval:reject']));

  // ⭐ P8.20：点击卡片（或键盘 Enter/Space）打开任务详情；操作按钮自行阻断冒泡。
  const handleOpen = () => onOpen?.(item);
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  return (
    <div
      className={[
        'appr-card',
        item.canIAct ? 'appr-card--mine' : '',
        onOpen ? 'appr-card--clickable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `查看任务详情：${item.taskName ?? `任务 #${item.taskId}`}` : undefined}
      onClick={onOpen ? handleOpen : undefined}
      onKeyDown={onOpen ? handleKeyDown : undefined}
    >
      <div className="appr-card__head">
        <span className="appr-card__name">{item.taskName ?? `任务 #${item.taskId}`}</span>
        <Badge tone={STATUS_TONE[item.status] ?? 'idle'}>
          {STATUS_LABEL[item.status] ?? item.status}
        </Badge>
        {overdue && <Badge tone="alert">紧急</Badge>}
      </div>

      <div className="appr-card__meta">
        <span>项目：{item.projectName ?? '—'}</span>
        {item.taskCode && <span className="num">编号 {item.taskCode}</span>}
      </div>

      {currentNode && (
        <div className="appr-card__node">
          节点：<b>{currentNode.name}</b>（第 {currentNode.seq}/{total} 节点）
          {currentNode.approverRoleCodes.length > 0 && (
            <span className="appr-card__roles">
              {' '}· 审批角色 {currentNode.approverRoleCodes.join('、')}
            </span>
          )}
        </div>
      )}

      <div className="appr-card__foot">
        <span className="appr-card__started">
          发起：{item.startedByName ?? '—'} {fmtAgo(item.startedAt)}
        </span>
        {sla && (
          <span className={['appr-card__sla', overdue ? 'is-over' : ''].join(' ')}>
            SLA：{sla}
          </span>
        )}

        {actionabled && isSubmitter && (
          <span className="appr-card__wait">该任务由你提交，需由他人复核</span>
        )}
        {actionabled && !isSubmitter && item.canIAct && (
          <span className="appr-card__btns">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onApprove?.(item);
              }}
            >
              同意
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onReject?.(item);
              }}
            >
              退回
            </Button>
          </span>
        )}
        {actionabled && !isSubmitter && !item.canIAct && (
          <span className="appr-card__wait">等待其他审批人</span>
        )}
        {canTerminate && (
          <span className="appr-card__btns">
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onTerminate?.(item);
              }}
            >
              终止超时审批
            </Button>
          </span>
        )}
      </div>
    </div>
  );
}
